import React from "react";
import { interpolate, spring } from "remotion";
import { COLORS, FONTS, SLOT, SPRING } from "./constants";

export function glow(c: string, strong = false): string {
  return strong
    ? `0 0 24px ${c}cc, 0 0 60px ${c}77, 0 0 120px ${c}33`
    : `0 0 16px ${c}99, 0 0 40px ${c}44`;
}

/* Shared backdrop — deep grid + orbs + particles */
export const DemoBackdrop: React.FC<{ frame: number; accent: string }> = ({ frame, accent }) => {
  const drift = Math.sin(frame * 0.005) * 6;
  return (
    <>
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse 80% 60% at 50% 45%, ${accent}0d 0%, transparent 70%)`,
      }} />
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${accent}0a 1px, transparent 1px), linear-gradient(90deg, ${accent}0a 1px, transparent 1px)`,
        backgroundSize: "80px 80px",
        transform: `translateY(${drift}px)`,
      }} />
      {[...Array(4)].map((_, i) => {
        const speed = 0.004 + i * 0.001;
        const cx = [220, 1680, 340, 1600][i];
        const cy = [200, 280, 820, 780][i];
        const c = [accent, COLORS.neonPurple, COLORS.neonBlue, COLORS.neonPink][i];
        const s = 480 - i * 40;
        return (
          <div key={i} style={{
            position: "absolute",
            left: cx + Math.sin(frame * speed + i) * 30 - s / 2,
            top: cy + Math.cos(frame * speed * 0.7 + i) * 20 - s / 2,
            width: s, height: s, borderRadius: "50%",
            background: `radial-gradient(circle, ${c}08 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
        );
      })}
      {[...Array(10)].map((_, i) => {
        const px = 120 + ((i * 173) % 1720);
        const py = 80 + ((i * 103) % 940);
        const colors = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonCyan, COLORS.neonPurple];
        const c = colors[i % colors.length];
        return (
          <div key={`p-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i) * 18,
            top: py + Math.cos(frame * 0.015 + i) * 14,
            width: 4, height: 4,
            borderRadius: "50%", background: c,
            boxShadow: `0 0 12px ${c}`,
            opacity: 0.55,
          }} />
        );
      })}
    </>
  );
};

/* The "drop your OBS capture here" placeholder rect.
 * Sized to SLOT — users replace this with <OffthreadVideo /> or similar later.
 */
export const OBSSlot: React.FC<{
  frame: number;
  fps: number;
  accent: string;
  filename: string;
  step: string;
}> = ({ frame, fps, accent, filename, step }) => {
  const sp = spring({ frame: frame - 20, fps, config: SPRING.gentle });
  const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const scale = interpolate(sp, [0, 1], [0.92, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const borderPulse = 1 + Math.sin(frame * 0.06) * 0.06;

  return (
    <div
      data-obs-slot={filename}
      data-obs-step={step}
      style={{
        position: "absolute",
        left: SLOT.x,
        top: SLOT.y,
        width: SLOT.w,
        height: SLOT.h,
        opacity: op,
        transform: `scale(${scale * borderPulse})`,
        background: COLORS.bgCardLight,
        border: `3px dashed ${accent}`,
        borderRadius: 22,
        boxShadow: `0 0 60px ${accent}55, 0 0 120px ${accent}22, inset 0 0 80px ${accent}0a`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
      }}
    >
      {/* Corner markers */}
      {[
        { top: 20, left: 20, rot: 0 },
        { top: 20, right: 20, rot: 90 },
        { bottom: 20, right: 20, rot: 180 },
        { bottom: 20, left: 20, rot: 270 },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: 50,
            height: 50,
            borderTop: `4px solid ${accent}`,
            borderLeft: `4px solid ${accent}`,
            transform: `rotate(${pos.rot}deg)`,
            ...pos,
          }}
        />
      ))}

      {/* Placeholder content */}
      <div style={{
        fontSize: 72,
        filter: `drop-shadow(0 0 30px ${accent}cc)`,
      }}>🎥</div>
      <div style={{
        fontSize: 28,
        fontFamily: FONTS.mono,
        color: accent,
        letterSpacing: 3,
        textTransform: "uppercase",
        textShadow: glow(accent, true),
        fontWeight: 800,
      }}>
        OBS RECORDING SLOT
      </div>
      <div style={{
        fontSize: 22,
        fontFamily: FONTS.mono,
        color: COLORS.textMuted,
        letterSpacing: 1,
      }}>
        Drop: <span style={{ color: accent }}>{filename}</span>
      </div>
      <div style={{
        fontSize: 18,
        fontFamily: FONTS.body,
        color: COLORS.textSecondary,
        maxWidth: 900,
        textAlign: "center",
        lineHeight: 1.5,
        padding: "0 40px",
        fontStyle: "italic",
      }}>
        {step}
      </div>
    </div>
  );
};

/* Demo scene top title bar — outside the SLOT area */
export const DemoTitle: React.FC<{
  chapter: string;
  title: string;
  color: string;
  frame: number;
  appear: number;
}> = ({ chapter, title, color, frame, appear }) => (
  <div style={{
    position: "absolute",
    top: 40,
    left: 0,
    right: 0,
    textAlign: "center",
    opacity: appear,
    transform: `translateY(${(1 - appear) * -20}px)`,
    zIndex: 20,
  }}>
    <div style={{
      display: "inline-block",
      fontSize: 18,
      fontFamily: FONTS.mono,
      color,
      letterSpacing: 3,
      textTransform: "uppercase",
      padding: "6px 20px",
      borderRadius: 30,
      background: `${color}15`,
      border: `1.5px solid ${color}aa`,
      textShadow: glow(color),
      marginBottom: 8,
    }}>
      {chapter}
    </div>
    <div style={{
      fontSize: 48,
      fontFamily: FONTS.heading,
      fontWeight: 900,
      color: COLORS.textPrimary,
      letterSpacing: -1,
      textShadow: `0 0 30px ${color}88`,
    }}>
      {title}
    </div>
  </div>
);

/* Bottom caption bar under the slot — short VO hook */
export const DemoCaption: React.FC<{
  text: string;
  color: string;
  frame: number;
  fps: number;
  delay?: number;
}> = ({ text, color, frame, fps, delay = 30 }) => {
  const sp = spring({ frame: frame - delay, fps, config: SPRING.gentle });
  const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{
      position: "absolute",
      bottom: 28,
      left: 0, right: 0,
      textAlign: "center",
      opacity: op,
      zIndex: 20,
    }}>
      <div style={{
        display: "inline-block",
        fontSize: 26,
        fontFamily: FONTS.heading,
        fontWeight: 700,
        color,
        padding: "12px 40px",
        borderRadius: 14,
        background: `${COLORS.bgCard}cc`,
        border: `1.5px solid ${color}77`,
        textShadow: glow(color),
        letterSpacing: 0.5,
      }}>
        {text}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
